import type { GrantedObject, HttpMethod } from "../../api/entitlementsAdminApi";
import { HTTP_METHODS, defaultMethodAccess } from "../PermissionObject/permissionObjectUtils";

const METHOD_ORDER: string[] = HTTP_METHODS;

const rank = (k: string) => {
  const i = METHOD_ORDER.indexOf(k);
  return i === -1 ? METHOD_ORDER.length : i;
};

const sortedMethods = (m: Record<string, string>) =>
  Object.keys(m || {}).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));

export const groupObjectsByRole = (
  objects: GrantedObject[],
): Record<string, GrantedObject[]> =>
  objects.reduce<Record<string, GrantedObject[]>>((acc, o) => {
    (acc[o.via_role] ||= []).push(o);
    return acc;
  }, {});

const LEVEL_RANK: Record<string, number> = { read: 1, write: 2, admin: 3 };

/**
 * For each method an object requires, whether the role's level on the
 * endpoints group satisfies it (read < write < admin).
 */
export const methodAccessStatus = (
  m: Record<string, string>,
  roleLevel: string,
): { method: string; required: string; met: boolean }[] => {
  const have = LEVEL_RANK[(roleLevel || "").toLowerCase()] ?? 0;
  return sortedMethods(m).map((method) => ({
    method,
    required: m[method],
    met: have >= (LEVEL_RANK[(m[method] || "").toLowerCase()] ?? Infinity),
  }));
};

export interface MethodCellStatus {
  method: string;
  /** Lowercase level: the stored one, or the service default when none is stored. */
  level: string;
  /** "default": nothing is stored for this method, so the service default applies. */
  state: "met" | "unmet" | "default";
}

/** One method column of the API grid for one granted object. */
export const methodCellStatus = (
  o: Pick<GrantedObject, "method_access" | "access_level_name">,
  method: string,
): MethodCellStatus => {
  const s = methodAccessStatus(o.method_access, o.access_level_name).find(
    (x) => x.method === method,
  );
  if (s) {
    return { method, level: (s.required || "").toLowerCase(), state: s.met ? "met" : "unmet" };
  }
  return {
    method,
    level: defaultMethodAccess("API")[method as HttpMethod] || "",
    state: "default",
  };
};
